import React, { useEffect, useMemo, useState } from 'react';
import { FormControl, InputLabel, OutlinedInput } from '@mui/material';
import { useIntl } from 'react-intl';
import { PatternFormat } from 'react-number-format';
import { diffSecondsFromNow } from 'Util/DateUtils';

const VerificationNumberInput = ({ value, onValueChange, isError = false, errorText, expiresAt, handleVerifyPhone }) => {
  const { formatMessage } = useIntl();
  const [isFocus, setIsFocus] = useState(false);
  const [timer, setTimer] = useState(0);
  const [isTimer, setIsTimer] = useState(false);

  useEffect(() => {
    if (isTimer) {
      const interval = setInterval(() => {
        const diff = diffSecondsFromNow(expiresAt);
        if (diff <= 0) {
          setIsTimer(false);
          return;
        }
        setTimer(diff);
      }, 1000);
      return () => clearInterval(interval);
    }
  }, [isTimer, expiresAt]);

  useEffect(() => {
    if (expiresAt) {
      setTimer(diffSecondsFromNow(expiresAt));
      setIsTimer(true);
    }
  }, [expiresAt]);

  const placeholder = useMemo(() => {
    if (!isFocus) return formatMessage({ id: 'signIn.join.phone.verify.number' });
    return formatMessage({ id: 'signIn.join.phone.verify.input.placeholder' });
  }, [isFocus, formatMessage]);

  const showTimer = useMemo(() => {
    if (!isTimer) return '0:00';
    if (timer <= 0) return '0:00';
    return `${Math.floor(timer / 60)}:${String(timer % 60).padStart(2, '0')}`;
  }, [isTimer, timer]);

  return (
    <FormControl
      error={isError}
      fullWidth
      variant="outlined"
      sx={{
        '& label': {
          color: '#000',
          fontFamily: 'Pretendard',
        },
        '& label.Mui-focused': {
          color: '#000',
        },
        '& label.Mui-error': {
          color: '#000',
        },

        '& .MuiOutlinedInput-root': {
          fieldset: {
            borderRadius: '10px',
            border: '1px solid #ddd',
          },
          '&:hover fieldset': {
            borderColor: '#000',
          },
          '&.Mui-focused fieldset': {
            border: '1px solid #000',
          },
          '&.Mui-error fieldset': {
            border: '1px solid #FF4B4B',
          },
        },
      }}
    >
      {(isFocus || value) && <InputLabel htmlFor={'signIn.join.phone.verify.number'}>{formatMessage({ id: 'signIn.join.phone.verify.number' })}</InputLabel>}
      <PatternFormat
        id="signIn.join.phone.verify.number"
        value={value}
        onValueChange={onValueChange}
        format="######"
        type={'text'}
        placeholder={placeholder}
        label={formatMessage({ id: 'signIn.join.phone.verify.number' })}
        onFocus={() => {
          setIsFocus(true);
        }}
        onBlur={() => {
          setIsFocus(false);
        }}
        customInput={OutlinedInput}
        inputProps={{
          style: {
            color: '#222',
            fontFamily: 'Pretendard',
            fontSize: '15px',
            fontStyle: 'normal',
            fontWeight: 400,
            lineHeight: 'normal',
            padding: '15px',
          },
          tabIndex: 1,
        }}
        onKeyPress={e => {
          if (e.key === 'Enter') {
            handleVerifyPhone();
          }
        }}
        endAdornment={<div style={{ color: '#0567f9', fontSize: '15px', fontWeight: 400, lineHeight: 'normal', whiteSpace: 'nowrap' }}>{showTimer}</div>}
      />
      {isError && errorText && <div className="error-text">{errorText}</div>}
    </FormControl>
  );
};

export default VerificationNumberInput;
